<template>
  <div class="modal-backdrop" @click.self="close"><section class="record-detail-dialog" role="dialog" aria-modal="true" aria-labelledby="account-edit-title"><button class="modal-close" aria-label="关闭账户管理" :disabled="submitting" @click="close">×</button><h2 id="account-edit-title">{{ confirmingDelete ? '确认删除账户' : '管理收款账户' }}</h2>
    <PageState v-if="loading || error" :loading="loading" :error="error" @retry="load" />
    <template v-else-if="account">
      <template v-if="confirmingDelete"><p>确认删除「{{ account.alias }}」（{{ account.accountNoMasked }}）？删除后，该账户将不再出现在取款账户列表。</p><div class="detail-actions"><button class="subtle-button" :disabled="submitting" @click="confirmingDelete = false">返回</button><button class="primary" :disabled="submitting" @click="remove">{{ submitting ? '删除中…' : '确认删除' }}</button></div></template>
      <form v-else class="receipt-edit-form" @submit.prevent="save">
        <figure v-if="receiptQrUrl" class="saved-receipt-preview">
          <a :href="receiptQrUrl" target="_blank" rel="noopener noreferrer" aria-label="查看收款码大图"><img :src="receiptQrUrl" alt="收款码" /></a>
          <figcaption>收款码，点击查看大图</figcaption>
        </figure><p class="record-note">{{ accountTypeName || account.accountType }} · {{ account.accountNoMasked }}</p><label class="stacked-label">账户别名<input v-model.trim="form.alias" required maxlength="100" /></label><label class="stacked-label">账户姓名<input v-model.trim="form.accountName" required maxlength="100" /></label><label class="stacked-label">新账号或钱包地址<input v-model.trim="form.accountNo" maxlength="255" placeholder="留空保留原账号，请勿填写脱敏账号" autocomplete="off" /></label><template v-if="account.accountType === 'BANK_CARD'"><label class="stacked-label">银行名称<input v-model.trim="form.bankName" maxlength="100" /></label><label class="stacked-label">开户支行<input v-model.trim="form.branchName" maxlength="200" /></label></template><p class="record-note">币种：{{ account.currency }}<span v-if="account.network"> · 网络：{{ account.network }}</span></p><label class="inline-check"><input v-model="form.isDefault" type="checkbox" :true-value="1" :false-value="2" />设为默认账户</label><div class="detail-actions"><button type="button" class="reference-link" :disabled="submitting" @click="confirmingDelete = true; actionError = ''">删除账户</button><button class="primary" :disabled="submitting">{{ submitting ? '保存中…' : '保存修改' }}</button></div></form>
      <ErrorNotice v-if="actionError" class="field-error" :message="actionError"></ErrorNotice>
    </template>
  </section></div>
</template>
<script setup>
import { reportError } from '../../services/feedback.js'

import ErrorNotice from '../feedback/ErrorNotice.vue'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useAppContext } from '../../composables/appContext'
import { webApi } from '../../services/webApi'
import PageState from '../feedback/PageState.vue'
import { safeExternalUrl } from '../../config/navigation.js'
const props = defineProps({ accountId:{type:Number,required:true}, accountTypeName:String })
const emit = defineEmits(['close','saved'])
const { isLoggedIn, notify } = useAppContext()
const account = ref(null), loading = ref(false), error = ref(''), actionError = ref(''), submitting = ref(false), confirmingDelete = ref(false), form = ref({})
const receiptQrUrl = computed(() =>
  ['WECHAT', 'ALIPAY'].includes(account.value?.accountType)
    ? safeExternalUrl(account.value?.qrUrl)
    : ''
)
let version = 0
function close() { if (!submitting.value) emit('close') }
async function load() {
  const ticket = ++version
  loading.value = true; error.value = ''; account.value = null
  try {
    const result = await webApi.paymentAccountDetail(props.accountId)
    if (ticket !== version || !isLoggedIn.value) return
    account.value = result
    form.value = { alias:result.alias, accountName:result.accountName, accountNo:'', bankName:result.bankName || '', branchName:result.branchName || '', currency:result.currency, network:result.network || '', qrUrl:result.qrUrl || '', isDefault:result.isDefault === 1 ? 1 : 2 }
  } catch (e) { if (ticket === version) error.value = e.message || '账户加载失败' }
  finally { if (ticket === version) loading.value = false }
}
async function save() {
  if (submitting.value || !isLoggedIn.value) return
  if (!form.value.alias || !form.value.accountName) { actionError.value = reportError('请完整填写账户别名和姓名'); return }
  if (form.value.accountNo.includes('*')) { actionError.value = reportError('请填写完整新账号，或留空保留原账号'); return }
  const ticket = version
  submitting.value = true; actionError.value = ''
  try {
    await webApi.updatePaymentAccount(props.accountId, { ...form.value })
    if (ticket !== version || !isLoggedIn.value) return
    notify('账户已保存'); emit('saved'); emit('close')
  } catch (e) { if (ticket === version) actionError.value = e.message || '保存失败，已保留填写内容' }
  finally { submitting.value = false }
}
async function remove() {
  if (submitting.value || !isLoggedIn.value) return
  const ticket = version
  submitting.value = true; actionError.value = ''
  try {
    await webApi.deletePaymentAccount(props.accountId)
    if (ticket !== version || !isLoggedIn.value) return
    notify('账户已删除'); emit('saved'); emit('close')
  } catch (e) { if (ticket === version) actionError.value = e.message || '删除失败' }
  finally { submitting.value = false }
}
watch(() => props.accountId, load, { immediate:true })
watch(isLoggedIn, loggedIn => { if (!loggedIn) { version++; emit('close') } })
onBeforeUnmount(() => { version++ })
</script>
<style scoped>
.saved-receipt-preview { display: flex; align-items: center; gap: 16px; margin: 16px 0; }
.saved-receipt-preview img { display: block; width: 128px; height: 128px; object-fit: contain; border-radius: 8px; }
.saved-receipt-preview figcaption { font-size: 13px; }
</style>
